import { Table } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  convertISOToDateFormat,
  convertISOToDateTimeFormat
} from '../../../../util/timezone';
import { MultiDataColumn, MultiHeader } from '../../../common/components';
import { PreventionAlertHistoryContainer } from '../../ScoreExplainability/styled';
import style from './index.scss';
import { connect } from 'react-redux';
import { getAMLSCommonUnifiedAlertsPrevention } from '../../../../redux/actions/common';
import {
  fetchTypologiesActions,
  fetchAMLSAlertLevel
} from '../../../../redux/actions/common';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';
import { getAlertSourceName } from '../../../common/AlertHistory/FilterOption/constants';
import { FRAUD_PREVENTION_ENGINE_SOURCE_IDS } from '../../../constants/commonConstants';
import TablePagination from '../../../cm/common/TablePagination';
import {
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION,
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION_PRODUCTION
} from '../../../trm/testRunInstanceDashboard/ResultTab/constants';
import DateRangeWithPresets from '../../../cm/common/DateRangeWithPresets';
import { fromDaysToRangeBack } from '../../../cm/utils/common';
import { toLower } from '../../../../util/lodashlike';

const { from, to } = fromDaysToRangeBack(30);

// FIN-13233 — which column carries the triggered names depends on the ENGINE that raised
// the alert, not on the product grouping:
//
//   fraud-prevention engine (24, 25, 29) -> triggered_rules       [{ ruleName }]
//   every other product      (2, 6, 30…) -> triggered_typologies  [{ name }]
//
// This table read `triggeredRules` unconditionally, so AML/TM rows showed 0 variants and a
// blank name list even with typologies stored against the alert. Confirmed against
// keycloak-qa: source 6 carries triggered_typologies on all 143,543 alerts and zero
// triggered_rules; source 29 is the exact inverse.
//
// Sources 2 and 30 populate NEITHER column, so a blank list there is CORRECT and must stay
// blank — the reporter called this out explicitly. Returning [] (never a placeholder) keeps
// that true.
//
// The count and the name list both go through here so they can never disagree; previously
// they read the same field by coincidence rather than by construction.
export const getTriggeredNames = (record) => {
  if (!record) return [];
  const isFraudEngine = FRAUD_PREVENTION_ENGINE_SOURCE_IDS.includes(
    parseInt(record.alertSourceId, 10)
  );
  const source = isFraudEngine
    ? record.triggeredRules
    : record.triggeredTypologies;
  if (!Array.isArray(source)) return [];
  return source
    .map((entry) => (isFraudEngine ? entry?.ruleName : entry?.name))
    .filter((name) => name !== null && name !== undefined && name !== '');
};

const AlertHistoryPrevention = ({
  dispatch,
  alertPartyIdentity,
  alertHistory,
  trmActions,
  alertLevels,
  isLoading,
  isFromCM,
  primaryPartyKey,
  // FIN-11398: explicit customer key for the FRAML customer-aggregate detail view.
  // When present it takes TOP precedence over the Redux-derived alert scoping.
  customerKey,
  // FIN-12238: the currently-viewed alert id, excluded from its own history.
  currentAlertId
}) => {
  const [query, setQuery] = useState({
    from,
    to,
    currentPage: 1,
    pageSize: 10
  });

  // FIN-12133: colour-code the Action by its configured severity's level colour
  // (action_severity -> alert_level_details), consistent with the Score Explanation.
  const { getLevelColor } = useAlertLevelConfigs(alertLevels);

  useEffect(() => {
    dispatch(fetchTypologiesActions());
    if (!alertLevels?.length) {
      dispatch(fetchAMLSAlertLevel());
    }
  }, []);

  const preventionAlertColors = (key) =>
    getLevelColor(
      trmActions?.data?.find(
        ({ displayName }) => toLower(displayName) === toLower(key)
      )?.actionSeverity
    );

  useEffect(() => {
    // FIN-11398: explicit customerKey own-prop takes TOP precedence. The FRAML
    // customer-aggregate detail view has no alertDetails/alertPartyIdentity in the
    // store, so scope the alert-history fetch directly by the customer's key. The
    // trailing comma matches the plain-key convention below (survives the BE GET
    // param parser's comma-split). The single-alert (SmartAssessment / FIN-11542)
    // path is left untouched for callers that do NOT pass customerKey.
    if (customerKey) {
      if (query.from && query.to) {
        dispatch(
          getAMLSCommonUnifiedAlertsPrevention({
            customerId: `${customerKey},`,
            sourceIds: isFromCM
              ? UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION_PRODUCTION
              : UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION,
            limit: query.pageSize,
            offset: (query.currentPage - 1) * query.pageSize,
            from: query.from,
            to: query.to
          })
        );
      }
      return;
    }

    if (alertPartyIdentity?.data) {
      let uniqueCombinedKeys = new Set();

      Object.keys(alertPartyIdentity.data).forEach((partyIdentity) => {
        const partyKey = alertPartyIdentity.data[partyIdentity].partyKey;

        if (partyKey) {
          uniqueCombinedKeys.add(partyKey);
        }
      });

      // FIN-12137: scope Alert History by the alert's primary_party_key
      // (sender/receiver/intermediary hashcodes only), NOT the full party-identity
      // list — that list includes shared infrastructure parties (PSP wallet,
      // correspondent partners) that also appear on tens of thousands of unrelated
      // alerts, collapsing every customer to the same unscoped superset.
      //
      // FIN-12137 (follow-up): sending the raw primary_party_key JSON envelope
      // ({"sender_hashcode":..,"receiver_hashcode":..,"intermediary_hashcodes":[]})
      // as `customerId` is broken end to end — the alert-history GET request parser
      // (BE GetRequestParserHelper.buildJson) comma-splits ANY query value, so the
      // envelope is shattered into fragments BEFORE the BE's isJsonEnvelope check
      // runs; each fragment then fails that check, is treated as a literal key,
      // matches nothing, and the history collapses to NoMatch (0 alerts) for every
      // FRAML-P customer. So extract the envelope's hashcodes here and send them as
      // the plain comma-joined key list: this survives the param parser and still
      // scopes to Sender+Receiver(+intermediary) only, never the shared PSP/partner
      // keys that caused the original superset bug.
      const extractEnvelopeKeys = (envelope) => {
        try {
          const parsed = JSON.parse(envelope);
          return [
            parsed.sender_hashcode,
            parsed.receiver_hashcode,
            parsed.user_id,
            ...(Array.isArray(parsed.intermediary_hashcodes)
              ? parsed.intermediary_hashcodes
              : [])
          ].filter((key) => key && String(key).trim());
        } catch (e) {
          return [];
        }
      };

      const envelopeKeys =
        primaryPartyKey && primaryPartyKey.trim().startsWith('{')
          ? extractEnvelopeKeys(primaryPartyKey)
          : [];

      let scopedCustomerId;
      if (envelopeKeys.length) {
        scopedCustomerId = `${envelopeKeys.join(',')},`;
      } else if (primaryPartyKey && primaryPartyKey.trim()) {
        // Non-envelope (plain) primary key — send as-is.
        scopedCustomerId = `${primaryPartyKey},`;
      } else {
        // Envelope unavailable — fall back to the party-identity keys.
        scopedCustomerId = `${[...uniqueCombinedKeys].join(',')},`;
      }

      if (query.from && query.to && !alertPartyIdentity.isLoading) {
        dispatch(
          getAMLSCommonUnifiedAlertsPrevention({
            customerId: scopedCustomerId,
            sourceIds: isFromCM
              ? UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION_PRODUCTION
              : UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION,
            limit: query.pageSize,
            offset: (query.currentPage - 1) * query.pageSize,
            from: query.from,
            to: query.to
          })
        );
      }
    }
  }, [query, alertPartyIdentity, customerKey]);

  const columns = [
    {
      title: 'Alert',
      children: [
        {
          title: <MultiHeader header="Alert ID" subHeaders={['Alert Date']} />,
          ellipsis: true,
          width: '10%',
          render: (record) => {
            const { alertId = '-', alertDate = '-' } = record;
            if (record.alertId && record.alertDate) {
              return (
                <MultiDataColumn.Link
                  pop
                  data={[
                    {
                      text: alertId
                    },
                    {
                      text: convertISOToDateFormat(alertDate) || '-'
                    }
                  ]}
                  customLink={
                    isFromCM
                      ? '/app/cm/alerts/:alertId'
                      : '/app/trm/run-instance/:alertId'
                  }
                  customLinkParam=":alertId"
                  newTab={true}
                />
              );
            }
          }
        },
        {
          title: <MultiHeader header="Alert Source" subHeaders={['']} />,
          width: '7%',
          render: (record) => {
            const { alertSourceId } = record;
            // FIN-13233 — was a chain of `alertSourceId === PROD_IDS.X.ID` comparisons, but
            // those IDs are CSV GROUPS ('0,1,6', '2,3,30'), so a single id could never equal
            // one and every grouped product rendered '-'. The lookup expands the groups.
            const alertSourceName = getAlertSourceName(alertSourceId);
            return (
              <p
                style={{
                  color: '#2a2e39',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {alertSourceName}
              </p>
            );
          }
        },
        {
          title: <MultiHeader header="No of Variants" subHeaders={['']} />,
          width: '10%',
          render: (record) => {
            return (
              <p
                style={{
                  alignSelf: 'center',
                  color: '#2a2e39',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {getTriggeredNames(record).length}
              </p>
            );
          }
        },
        {
          title: (
            <MultiHeader
              header="Alert Level/Actions"
              subHeaders={['Alert Score/Count']}
            />
          ),
          width: '7%',
          render: (record) => {
            if (record.alertSourceId === '24') {
              let triggeredRulesActionSummary = {};
              try {
                triggeredRulesActionSummary = JSON.parse(
                  record.triggeredRulesActionSummary
                );
              } catch (error) {
                console.log('error in parsing JSON', error);
              }
              return (
                <>
                  {Object.keys(triggeredRulesActionSummary).map((key) => (
                    <div
                      key={key}
                      style={{
                        color: preventionAlertColors(key)
                      }}
                    >
                      {key}: {triggeredRulesActionSummary[key]}
                    </div>
                  ))}
                </>
              );
            }
            if (
              record.alertSourceId === '25' ||
              record.alertSourceId === '29'
            ) {
              const { additionalFraudInfo = '-' } = record;
              const keys = Object.keys(additionalFraudInfo);
              const values = Object.values(additionalFraudInfo);
              return (
                <>
                  {keys.map((key, index) => (
                    <div
                      key={key}
                      style={{
                        color: preventionAlertColors(key)
                      }}
                    >
                      {key}: {values[index]}
                    </div>
                  ))}
                </>
              );
            } else {
              return record.alertLevel;
            }
          }
        }
      ]
    },
    {
      title: 'Variant Names/Typologies Triggered',
      children: [
        {
          title: '',
          width: '10%',
          render: (record) => {
            const typologyNames = getTriggeredNames(record);

            return typologyNames.map((m, i) => (
              <p style={{ marginBottom: 0 }} key={`m-${i}`}>
                {m}
                {i !== typologyNames.length - 1 ? ',' : ''}
              </p>
            ));
          }
        }
      ]
    },
    {
      title: 'Alert Status',
      children: [
        {
          title: <MultiHeader header="Status" subHeaders={['Close Date']} />,
          width: '10%',
          render: ({ alertStatus, closureDate }) => {
            return (
              <MultiDataColumn
                data={[
                  { text: alertStatus || '-' },
                  {
                    text: closureDate
                      ? convertISOToDateTimeFormat(closureDate)
                      : '-'
                  }
                ]}
                pop
              />
            );
          }
        }
      ]
    }
  ];

  const handleChange = (currentPage, pageSize) => {
    setQuery({
      ...query,
      currentPage,
      pageSize
    });
  };

  const onDurationChange = (from, to) => {
    setQuery({
      ...query,
      from,
      to,
      currentPage: 1
    });
  };

  // FIN-12238 (FE-only): drop the currently-viewed alert from its own history.
  const rawHistory = alertHistory?.payload || [];
  const filteredHistory = currentAlertId
    ? rawHistory.filter((row) => row.alertId !== currentAlertId)
    : rawHistory;
  const rawTotal = alertHistory?.total || 0;
  const filteredTotal =
    currentAlertId && rawTotal > 0 ? rawTotal - 1 : rawTotal;

  return (
    <PreventionAlertHistoryContainer>
      <div className={style.filters}>
        <DateRangeWithPresets
          from={query?.from}
          to={query?.to}
          isDisableFutureDates
          onChange={onDurationChange}
        />
      </div>
      <Table
        rowKey={({ alertId }) => alertId}
        columns={columns}
        // FIN-12238: exclude the currently-viewed alert from its own history at
        // display time (FE-only). Exactly one alert (the current one) is dropped
        // and it's always counted in the BE total, so total - 1 keeps the count
        // in sync; only its page placement drifts by one row.
        dataSource={filteredHistory}
        className={style.alertTable}
        loading={isLoading || alertPartyIdentity?.isLoading}
        pagination={{
          total: filteredTotal,
          current: query.currentPage,
          pageSize: query.pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handleChange,
          showTotal: (total) => (
            <TablePagination
              selectedItemsCount={null}
              total={total}
              currentPage={query.currentPage}
              pageSize={query.pageSize}
            />
          )
        }}
      />
    </PreventionAlertHistoryContainer>
  );
};

AlertHistoryPrevention.propTypes = {
  dispatch: PropTypes.func.isRequired,
  // Not required: the FIN-11398 customer-scoped path (customerKey) has no
  // alertPartyIdentity in scope.
  alertPartyIdentity: PropTypes.object,
  // FIN-11398: explicit customer key; top precedence for customer-aggregate scope.
  customerKey: PropTypes.string,
  alertHistory: PropTypes.object,
  trmActions: PropTypes.object,
  alertLevels: PropTypes.array,
  isLoading: PropTypes.bool,
  isFromCM: PropTypes.bool,
  // FIN-12137: the alert's primary_party_key JSON envelope, used to scope
  // Alert History to the alert's own parties.
  primaryPartyKey: PropTypes.string,
  // FIN-12238: the currently-viewed alert id, excluded from its own history.
  currentAlertId: PropTypes.string
};

function mapStateToProps(state) {
  const { unifiedAlertsPrevention = {} } = state.amlsCommon;
  const { data: alertHistory, error, isLoading } = unifiedAlertsPrevention;

  return {
    alertHistory,
    error,
    isLoading,
    trmActions: state.amlsCommon.commonTypologyAction,
    // FIN-12133: alert_level_details configs to colour-code the Action by severity.
    alertLevels: state.amlsCommon.alertLevelConfigs?.data || [],
    // FIN-12137: primary_party_key envelope to scope Alert History to this alert's parties.
    primaryPartyKey: state.amlsCommon.alertDetails?.data?.primaryPartyKey,
    // FIN-12238: the currently-viewed alert id, excluded from its own history.
    currentAlertId: state.amlsCommon.alertDetails?.data?.alertId
  };
}

export default connect(mapStateToProps)(AlertHistoryPrevention);
